import { newE2EPage } from "@stencil/core/testing";

const testMenuItemsFlat = [
    {
        slug: "menu-item-1",
        label: "Menu Item 1"
    },
    {
        slug: "menu-item-2",
        label: "Menu Item 2"
    },
    {
        slug: "menu-item-3",
        label: "Menu Item 3",
        icon: "uic:apps"
    },
    {
        slug: "menu-item-4",
        label: "",
        icon: "uic:user"
    }
];

const testMenuItemsNested = [
    {
        slug: "menu-item-1",
        label: "Menu Item 1",
        items: [
            {
                slug: "menu-item-1-1",
                label: "Menu Item 1-1",
                items: [
                    {
                        slug: "menu-item-1-1-1",
                        label: "Menu Item 1-1-1"
                    },
                    {
                        slug: "menu-item-1-1-2",
                        label: "Menu Item 1-1-2"
                    }
                ]
            }
        ]
    }
];

const testMenuItemsToggle = [
    {
        slug: "toggle-1",
        label: "Toggle 1",
        toggle: true
    },
    {
        slug: "toggle-2",
        label: "",
        icon: "uic:notifications",
        toggle: true,
        badge: 100
    }
];

describe("uic-menu", () => {
    it("should handle mouse and keyboard events correctly", async () => {
        const page = await newE2EPage();
        await page.setContent(`<uic-menu name="test-menu" current="menu-item-2"></uic-menu>`);

        const menu = await page.find("uic-menu.hydrated");
        menu.setProperty("items", testMenuItemsFlat);
        await page.waitForChanges();

        const item2 = await page.find("uic-menu-item[slug=menu-item-2].hydrated");
        const item3 = await page.find("uic-menu-item[slug=menu-item-3].hydrated");

        // Keyboard events
        await item2.focus();
        await item2.press("ArrowRight");
        await item3.press("Enter");
        await page.waitForChanges();
        expect(menu).toMatchSnapshot();
    });

    it("should render nested menu items correctly", async () => {
        const page = await newE2EPage();
        await page.setContent(`
            <uic-menu name="test-menu"></uic-menu>`);
        const menu = await page.find("uic-menu.hydrated");

        menu.setProperty("items", testMenuItemsNested);
        await page.waitForChanges();

        const rootItem = await page.find("uic-menu-item[slug=menu-item-1].hydrated");
        let nestedItem;
        // expand level 1
        await rootItem.click();
        await page.waitForChanges();
        // expand level 2
        nestedItem = await page.find("uic-menu-item[slug=menu-item-1-1].hydrated");
        await nestedItem.click();
        await page.waitForChanges();

        // select nested item
        nestedItem = await page.find("uic-menu-item[slug=menu-item-1-1-2].hydrated");
        await nestedItem.click();
        await page.waitForChanges();

        // is menu updated correctly
        expect(rootItem.getProperty("selected")).toBeTruthy();

        expect(menu).toMatchSnapshot();
    });

    xit("should update selected items if current is initially set", async () => {
        const page = await newE2EPage();
        await page.setContent(`
            <uic-menu name="test-menu" current="menu-item-1-1-2"></uic-menu>`);
        const menu = await page.find("uic-menu.hydrated");

        menu.setProperty("items", testMenuItemsNested);
        await page.waitForChanges();

        const rootItem = await page.find("uic-menu-item[slug=menu-item-1].hydrated");
        const nestedItem1 = await page.find("uic-menu-item[slug=menu-item-1-1-1].hydrated");
        const nestedItem2 = await page.find("uic-menu-item[slug=menu-item-1-1-2].hydrated");

        expect(rootItem.getAttribute("selected")).not.toBeNull();
        expect(nestedItem1.getAttribute("selected")).toBeNull();
        expect(nestedItem2.getAttribute("selected")).not.toBeNull();
    });

    it("should update selected items if current changes", async () => {
        const page = await newE2EPage();
        await page.setContent(`
            <uic-menu name="test-menu"></uic-menu>`);
        const menu = await page.find("uic-menu.hydrated");

        menu.setProperty("items", testMenuItemsNested);
        await page.waitForChanges();

        const rootItem = await page.find("uic-menu-item[slug=menu-item-1].hydrated");
        const nestedItem1 = await page.find("uic-menu-item[slug=menu-item-1-1-1].hydrated");
        const nestedItem2 = await page.find("uic-menu-item[slug=menu-item-1-1-2].hydrated");

        menu.setProperty("current", "");
        await page.waitForChanges();
        expect(rootItem.getAttribute("selected")).toBeNull();
        expect(nestedItem1.getAttribute("selected")).toBeNull();
        expect(nestedItem2.getAttribute("selected")).toBeNull();

        menu.setProperty("current", "menu-item-1-1-1");
        await page.waitForChanges();
        expect(rootItem.getAttribute("selected")).not.toBeNull();
        expect(nestedItem1.getAttribute("selected")).not.toBeNull();
        expect(nestedItem2.getAttribute("selected")).toBeNull();
    });

    it("should emit uicMenuClick and uicMenuChange events if a menu item is clicked", async () => {
        const page = await newE2EPage();
        await page.setContent(`
            <uic-menu name="test-menu"></uic-menu>`);

        const uicMenuClick = await page.spyOnEvent("uicMenuClick");
        const uicMenuChange = await page.spyOnEvent("uicMenuChange");

        const menu = await page.find("uic-menu.hydrated");

        menu.setProperty("items", testMenuItemsFlat);
        await page.waitForChanges();

        const item2 = await page.find("uic-menu-item[slug=menu-item-2].hydrated");
        const item3 = await page.find("uic-menu-item[slug=menu-item-3].hydrated");

        await item2.click();

        expect(uicMenuClick).toHaveReceivedEventTimes(1);
        expect(uicMenuChange).toHaveReceivedEventTimes(1);

        await item3.click();

        expect(uicMenuClick).toHaveReceivedEventTimes(2);
        expect(uicMenuChange).toHaveReceivedEventTimes(2);

        await item3.click();

        expect(uicMenuClick).toHaveReceivedEventTimes(3);
        expect(uicMenuChange).toHaveReceivedEventTimes(2);
    });

    it("should emit uicMenuToggle event if a toggle menu item is clicked", async () => {
        const page = await newE2EPage();
        await page.setContent(`
            <uic-menu name="test-menu"></uic-menu>`);

        const uicMenuChange = await page.spyOnEvent("uicMenuChange");
        const uicMenuToggle = await page.spyOnEvent("uicMenuToggle");

        const menu = await page.find("uic-menu.hydrated");

        menu.setProperty("items", testMenuItemsToggle);
        await page.waitForChanges();

        const toggle1 = await page.find("uic-menu-item[slug=toggle-1].hydrated");
        const toggle2 = await page.find("uic-menu-item[slug=toggle-2].hydrated");

        expect(toggle1.getAttribute("toggle")).not.toBeNull();
        expect(toggle2.getAttribute("toggle")).not.toBeNull();
        expect(toggle1.getAttribute("selected")).toBeNull();
        expect(toggle2.getAttribute("selected")).toBeNull();

        await toggle1.click();

        expect(uicMenuToggle).toHaveReceivedEventTimes(1);
        expect(uicMenuChange).toHaveReceivedEventTimes(0);

        expect(toggle1.getAttribute("selected")).not.toBeNull();
        expect(toggle2.getAttribute("selected")).toBeNull();

        await toggle2.click();

        expect(uicMenuToggle).toHaveReceivedEventTimes(2);
        expect(uicMenuChange).toHaveReceivedEventTimes(0);

        expect(toggle1.getAttribute("selected")).toBeNull();
        expect(toggle2.getAttribute("selected")).not.toBeNull();

        expect(menu).toMatchSnapshot();
    });
});
